<template>
  <div>
    <b-row>
      <b-col>
        <base-input class="mb-3" label="Transferor">
          <el-select
            ref="transferorName"
            v-model="transferorId"
            filterable
            placeholder="Transferor name"
            default-first-option
          >
            <el-option
              v-for="shareholder in currentShareholders"
              :key="shareholder.id"
              :label="shareholder.name"
              :value="shareholder.id"
            ></el-option>
          </el-select>
        </base-input>
      </b-col>

      <b-col>
        <base-input label="Share class" name="Share class">
          <select
            class="form-control"
            v-model="shareClassId"
            :disabled="!transferorId"
          >
            <option
              v-for="(holding, shareClassId) in transferor.shareholdingByClass"
              :value="shareClassId"
              :key="shareClassId"
              >{{ holding.shareClass.name | decode }}</option
            >
          </select>
        </base-input>
      </b-col>

      <b-col>
        <find-or-create-contact-form
          ref="findOrCreateContactForm"
          search-label="Recipient name"
          v-model="recipientId"
          :exclude-contact-ids="[transferorId]"
          :accountId="accountId"
        ></find-or-create-contact-form>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <base-input
          label="Allotment Date"
          name="Allotment Date"
          type="date"
          v-model="date"
        ></base-input>
      </b-col>

      <b-col>
        <!-- TODO: set max value from total shareholding of selected shareholder class -->
        <base-input
          type="number"
          v-model="numberOfShares"
          min="1"
          :max="maxShares"
          :disabled="!shareClassId"
        >
          <label class="form-control-label" slot="label"
            >Shares
            <i
              class="fa fa-question-circle ml-1"
              v-b-tooltip.hover
              title="Number of shares"
            ></i
          ></label>
        </base-input>
      </b-col>

      <b-col>
        <base-input
          type="text"
          v-model="recipientLedgerNumber"
          :disabled="recipientLedgerNumberEntryDisabled"
          ><label class="form-control-label" slot="label"
            >Ledger
            <i
              class="fa fa-question-circle ml-1"
              v-b-tooltip.hover
              title="Shareholder ledger number"
            ></i></label
        ></base-input>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="12" sm="6">
        <b-btn @click="$emit('cancel')" variant="outline-secondary" block>
          <i class="fa fa-times mr-2"></i> Cancel
        </b-btn>
      </b-col>
      <b-col cols="12" sm="6">
        <b-btn @click="create" variant="success" block>
          <span v-if="!creating">
            Save
            <i class="fa fa-arrow-right ml-2"></i>
          </span>
          <span v-else>
            <i class="fa fa-spin fa-spinner mr-2"></i> Please wait&hellip;
          </span>
        </b-btn>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import moment from "moment";
import _ from "underscore";
import { get } from "dot-prop";
import { Select, Option } from "element-ui";

import FindOrCreateContactForm from "../../Contacts/FindOrCreateContactForm";

export default {
  name: "share-allotment-create-form",
  components: {
    FindOrCreateContactForm,
    [Select.name]: Select,
    [Option.name]: Option
  },
  props: {
    accountId: {
      type: String,
      description: "Account ID",
      required: true
    },
    companyId: {
      type: String,
      description: "Company ID",
      required: true
    },
    currency: {
      type: String,
      description: "ISO Currency Code",
      required: true
    },
    shareClasses: {
      type: Array,
      description: "Share classes",
      required: true
    },
    shareholders: {
      type: Array,
      description: "Shareholders",
      required: true
    }
  },
  computed: {
    transferor() {
      if (!this.transferorId) {
        return { shareClasses: [] };
      } else {
        return _.findWhere(this.shareholders, { id: this.transferorId });
      }
    },
    currentShareholders() {
      return _.where(this.shareholders, { isCurrent: true });
    },
    maxShares() {
      if (!this.shareClassId) {
        return null;
      }

      const shareholdingOfSelectedClass = get(
        this.transferor,
        `shareholdingByClass.${this.shareClassId}`
      );
      if (shareholdingOfSelectedClass) {
        return shareholdingOfSelectedClass.totalShares;
      }
    }
  },
  methods: {
    setFocus() {
      this.$nextTick(() => {
        // focus on input
        this.$refs.transferorName.focus();
      });
    },
    setDefaultRecipientLedgerNumber() {
      const nextLedgerNumber = this.shareholders.length + 1;
      this.recipientLedgerNumber = nextLedgerNumber.toString().padStart(3, "0");
    },
    create() {
      this.creating = true;
      const requestBody = {
        type: "transfer",
        shareClassId: this.shareClassId,
        transferorId: this.transferorId,
        recipientId: this.recipientId,
        date: this.date,
        recipientLedgerNumber: this.recipientLedgerNumber,
        numberOfShares: this.numberOfShares
      };

      this.axios
        .post(
          `/v1/accounts/${this.accountId}/companies/${this.companyId}/share-movements`,
          requestBody
        )
        .then(response => {
          this.creating = false;
          if (response.data) {
            const shareMovement = response.data;
            this.$emit("share-movement-created", shareMovement);
          }

          this.$notify({
            message: "Share movement created",
            timeout: 3000,
            icon: "fa fa-check",
            type: "success"
          });
        })
        .catch(error => {
          this.creating = false;
          this.errorMessage = "Failed to create share movement";
          if (get(error, "response.data.message")) {
            this.errorMessage = error.response.data.message;
          }
        });
    }
  },
  data() {
    return {
      transferorId: null,
      recipientId: null,
      shareClassId: null,
      recipientLedgerNumber: null,
      recipientLedgerNumberEntryDisabled: false,
      creating: false,
      errorMessage: null,
      date: moment().format("YYYY-MM-DD"),
      numberOfShares: 1,
      fullyPaidUp: true
    };
  },
  created() {
    if (this.shareClasses.length) {
      this.shareClassId = this.shareClasses[0].id;
    }

    // set default recipient ledger number
    this.setDefaultRecipientLedgerNumber();
  },
  watch: {
    recipientId(newVal) {
      if (newVal) {
        // check if recipient is already a shareholder
        const existingShareholder = _.findWhere(this.shareholders, {
          id: newVal
        });
        if (existingShareholder) {
          this.recipientLedgerNumber = existingShareholder.ledgerNumber;
          this.recipientLedgerNumberEntryDisabled = true;
        } else {
          this.recipientLedgerNumberEntryDisabled = false;
          this.setDefaultRecipientLedgerNumber();
        }
      } else {
        this.recipientLedgerNumberEntryDisabled = false;
        this.setDefaultRecipientLedgerNumber();
      }
    },
    transferorId(newVal) {
      if (newVal) {
        if (this.numberOfShares > this.maxShares) {
          this.numberOfShares = this.maxShares;
        }
      }
    }
  }
};
</script>
